import Swal from 'sweetalert2';

// Status popups ("Note added successfully", "Saved", "Upload Failed"...) are fired all over
// the app as timed Swal.fire calls, many without `toast: true` — so they open as full-size
// centered modals. Any popup that dismisses itself on a timer and asks nothing of the user
// is turned into a compact toast here (styled in App.css), so every call site gets the same
// treatment, including ones added later. Anything with buttons to press or input to fill in
// is left as a real dialog. Imported once from index.js, before anything can fire.
const originalFire = Swal.fire;

// Every toast lands in one place, whatever position its call site asked for: the spot the
// tabletop's dice-result toast uses (TableTop.js) - 15px below the top of the tabletop view,
// under the video dock when it's open, centered on the view rather than the window.
// Swal's own 'top' sits at the very top of the viewport, behind the fixed 60px header.
const TOAST_POSITION = 'top';
const HEADER_HEIGHT = 60;
const TOAST_GAP = 15;

// Toasts sit above everything else on screen: the header, the tabletop's paused overlay
// (2000000000), MUI and Bootstrap modals, the help overlay. This is the largest z-index a
// browser accepts; things that also use it (the header) are earlier in the DOM than a
// toast mounted at the end of <body>, so the toast still wins the tie. Swal's own
// container sits at 1060, under most of the app's dialogs and overlays.
export const TOAST_Z_INDEX = 2147483647;

// Viewport point (fixed-position px) where a toast's top center goes. Shared with ToastPortal
// so the tabletop's own toasts (dice, encounter) land in the same spot as Swal's.
export function toastAnchor() {
    const view = document.querySelector('.tabletop-view');
    if (!view) return {top: HEADER_HEIGHT + TOAST_GAP, left: window.innerWidth / 2};
    const rect = view.getBoundingClientRect();
    const tableTop = document.getElementById('table-top');
    const dockOffset = tableTop ? parseFloat(getComputedStyle(tableTop).marginTop) || 0 : 0;
    return {top: rect.top + dockOffset + TOAST_GAP, left: rect.left + rect.width / 2};
}

// Runs before the toast is shown, so it never flashes at Swal's default spot. The
// container is already centered on `left` by Swal (left: 50% + translateX(-50%)).
function placeToast(popup) {
    const container = popup.parentElement;
    if (!container) return;
    const {top, left} = toastAnchor();
    container.style.top = `${top}px`;
    container.style.left = `${left}px`;
    container.style.paddingTop = '0';
    container.style.zIndex = String(TOAST_Z_INDEX);
}

const asToast = (opts, extra) => ({
    ...opts,
    ...extra,
    position: TOAST_POSITION,
    willOpen: (popup) => {
        placeToast(popup);
        opts.willOpen?.(popup);
    },
});

const isStatusPopup = (opts) =>
    opts.timer > 0 && !opts.input && !opts.showCancelButton && !opts.showDenyButton && opts.showConfirmButton !== true;

Swal.fire = function fire(...args) {
    const opts = args[0];
    if (args.length === 1 && opts && typeof opts === 'object' && !Array.isArray(opts)) {
        if (opts.toast) {
            args = [asToast(opts)];
        } else if (isStatusPopup(opts)) {
            args = [asToast(opts, { toast: true, showConfirmButton: false })];
        }
    }
    return originalFire.apply(this, args);
};
